import { LineChart } from "components/charts"
import {
  ComponentState,
  DateRangePicker,
  Icon,
  Modal,
  notification,
  Number,
  Skeleton,
  Space,
  Switch,
  Text,
  Title,
} from "components/common"
import { useLazyGetTSRComparisonQuery } from "../KPITSR/api"
import { useLazyGetSalesComparisonQuery } from "../KPISales/api"
import { useLazyGetAiCallAnalysisComparisonQuery } from "../AiCallAnalysis/api"
import Flex from "components/common/flex"
import { Separator } from "components/custom"
import { DATE_FORMAT, DATE_MONTH_FORMAT } from "constants"
import dayjs from "dayjs"
import { t } from "i18next"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { convertToHMSString, createDateList, joinBytItems, localisedValue, numberConvertor } from "utils"
import { dateTimeFunction } from "utils/date"
import theme from "utils/themeVars"

const ComparityModal = ({ parentIndex, dataIndex, comparityValue, slopeValue, rowData, colData, reportType }) => {
  const modalRef = useRef()
  const [comparityDate, setComparityDate] = useState()
  const [kpiCount, setKpiCount] = useState()
  const [totalNumberOfDays, setTotalNumberOfDays] = useState(0)
  const [isChecked, setIsChecked] = useState(
    reportType == "kpi_report_sales" ? !!rowData?.selfSeller : !rowData?.isLineManager
  )
  const [searchParams, setSearchParams] = useSearchParams()
  const queryObj = useMemo(() => Object.fromEntries([...searchParams]), [searchParams])
  const dateDiff =
    dayjs(decodeURIComponent(queryObj?.toDate || 0)).diff(dayjs(decodeURIComponent(queryObj?.fromDate || 0)), "day") + 1

  const showBenchMark = useCallback(
    () => {
      return (
        reportType === "kpi_report_sales" &&
        !!rowData?.selfSeller &&
        (queryObj?.staffType === "individual" || !rowData?.children?.length)
      )
    },
    queryObj,
    reportType
  )

  const getComparityHook = reportType => {
    switch (reportType) {
      case "kpi_report_telesales":
        return useLazyGetTSRComparisonQuery
      case "kpi_report_sales":
        return useLazyGetSalesComparisonQuery
      case "kpi_ai_analysis":
        return useLazyGetAiCallAnalysisComparisonQuery
      default:
        notification.error({ message: "Comparity is not supported for this report type" })
    }
  }

  const useComparity = getComparityHook(reportType)
  const [
    getComparity,
    { data: comparisonData, isFetching: fetchingComparison, error: comparisonError, isError: isComparisonError },
  ] = useComparity()

  const timeComparisonData = useMemo(() => {
    if (!comparisonData || fetchingComparison) return []
    const dataList = []
    let kpiSum = 0
    let kpiNumberOfDays = 0
    const data = comparisonData?.comparison?.data
    const dateList =
      Object.keys(comparisonData?.comparison?.data)?.length && Object.keys(comparisonData?.comparison?.data)
    const label = comparisonData?.comparison?.staticKeys?.[parentIndex]?.[dataIndex]?.label
    dateList?.forEach(key => {
      dataList.push(
        {
          name: label,
          date: dateTimeFunction({ date: key, format: DATE_FORMAT, apiRequest: true }),
          number: parseInt(data?.[key]?.[parentIndex]?.[dataIndex] ?? 0, 10),
        },
        ...(!!showBenchMark()
          ? [
              {
                name: "Top Performer",
                date: dateTimeFunction({ date: key, format: DATE_FORMAT, apiRequest: true }),
                number: parseInt(data?.[key]?.topPerformer?.[dataIndex] ?? 0, 10),
              },
            ]
          : [])
      )
      kpiSum += data?.[key]?.[parentIndex]?.[dataIndex] || 0
      kpiNumberOfDays += data?.[key]?.userActiveRosterDays || 0
    })
    setKpiCount(kpiSum)
    setTotalNumberOfDays(kpiNumberOfDays)
    return dataList
  }, [comparisonData, comparityDate, isChecked])

  const handleComparity = () => {
    comparityDate &&
      getComparity(
        {
          ...queryObj,
          fromDate: encodeURIComponent(
            dateTimeFunction({ date: comparityDate?.fromDate, timeZone: true, apiRequest: true })
          ),
          toDate: encodeURIComponent(
            dateTimeFunction({ date: comparityDate?.toDate, timeZone: true, apiRequest: true })
          ),
          includeRosterDays: isChecked,
          load: "comparison",
          userIds: rowData?.userId,
          ...(reportType === "kpi_report_sales" &&
            !!rowData?.selfSeller &&
            (queryObj?.staffType === "individual" || !rowData?.children?.length) && {
              benchmark: true,
              attribute: dataIndex,
            }),
        },
        true
      )
  }

  const onModalVisible = () => {
    setComparityDate({
      fromDate: rowData?.slopeRosterStartDate,
      toDate: rowData?.slopeRosterEndDate,
    })
  }
  const getTooltipContent = () => {
    const displaySlopeValue = () => {
      const formattedValue =
        colData.type === "time" ? convertToHMSString(Math.abs(slopeValue)) : Math.abs(slopeValue?.toFixed(2))
      const color = slopeValue > 0 ? theme["primary-color"] : slopeValue < 0 ? theme["error-color"] : theme["gray800"]
      return (
        <span style={{ color, fontWeight: 600 }}>
          {slopeValue > 0 ? "+" : slopeValue < 0 ? "-" : ""}
          {formattedValue}
        </span>
      )
    }
    let tooltipText = (
      <Flex vertical align="start" gap="6px" className="p-4">
        <div>
          <strong>{t("Avg Change")}: </strong>
          {comparityValue == 0 ? (
            t("No significant change")
          ) : (
            <>
              {displaySlopeValue()} {` ${colData.label}/${t("day")}`}
            </>
          )}
        </div>
        <div>
          <strong>{t("Day Range")}: </strong>
          {t("Last 30 working days")}
        </div>
      </Flex>
    )

    return tooltipText
  }

  const renderChartTooltip = item => {
    if (comparisonData?.comparison?.staticKeys?.[parentIndex]?.[dataIndex]?.type == "time") {
      return item?.map(chartItem => ({ ...chartItem, value: convertToHMSString(+chartItem?.value) }))
    }
    return item
  }

  const renderKPICounts = (kpiLabel, type) => {
    const formattedValue =
      type === "time" ? convertToHMSString(kpiCount) : <Number digitAfterFraction={2} isStringOnly value={kpiCount} />
    const averageValue =
      kpiCount && totalNumberOfDays && !colData?.avgKpi
        ? type === "time"
          ? convertToHMSString(Math.round(kpiCount / totalNumberOfDays))
          : (kpiCount / totalNumberOfDays)?.toFixed(2)
        : null

    return (
      <>
        <Title level={3} className="mbe-0">
          {kpiLabel}
        </Title>
        {!colData?.avgKpi && (
          <Flex align="center" justify="start" gap="4px">
            <Title className="mbe-0" level={3}>
              {formattedValue}
            </Title>
            {averageValue && <Text type="secondary">{`(Average ${averageValue})`}</Text>}
          </Flex>
        )}
      </>
    )
  }

  useEffect(() => {
    comparityDate && handleComparity()
  }, [comparityDate, isChecked])

  return (
    <span onClick={e => e.stopPropagation()}>
      {comparityValue != null && dateDiff <= 60 && (
        <Icon
          icon={comparityValue > 0 ? "HiArrowUp" : comparityValue < 0 ? "HiArrowDown" : "HiMinus"}
          color={
            comparityValue > 0 ? theme["primary-color"] : comparityValue < 0 ? theme["error-color"] : theme["gray800"]
          }
          onClick={() => {
            modalRef.current.setVisible(true)
          }}
          tooltipText={getTooltipContent()}
          size="14px"
          className="pointer"
        />
      )}
      <Modal
        ref={modalRef}
        footer={null}
        onCancel={e => {
          modalRef.current.setVisible(false)
        }}
        closable={false}
        onModalVisible={onModalVisible}
      >
        {!!isComparisonError ? (
          <ComponentState
            description={comparisonError.message}
            status={comparisonError.status}
            btnLoading={fetchingComparison}
            onClick={handleComparity}
          />
        ) : (
          <>
            <Title level={3} className="mbe-8">
              <Flex vertical align="start">
                <div className="gray-800 fs14">
                  {joinBytItems(
                    [rowData?.userName, localisedValue({ key: "designation", object: rowData }), rowData?.joiningDate],
                    <Separator />
                  )}
                </div>
              </Flex>
            </Title>
            <Flex justify="space-between" align="start" className="mbe-32">
              {fetchingComparison ? (
                <Skeleton />
              ) : (
                <Space direction="vertical">
                  {renderKPICounts(
                    comparisonData?.comparison?.staticKeys?.[parentIndex]?.[dataIndex]?.label,
                    comparisonData?.comparison?.staticKeys?.[parentIndex]?.[dataIndex]?.type
                  )}
                </Space>
              )}
              <Flex vertical align="end" justify="center" gap="12px">
                <DateRangePicker
                  disabled={fetchingComparison}
                  value={[comparityDate?.fromDate, comparityDate?.toDate]}
                  onChange={dates => {
                    setComparityDate({
                      fromDate: dayjs(dates[0]),
                      toDate: dayjs(dates[1]),
                    })
                  }}
                />
                <Switch
                  value={isChecked}
                  disabled={fetchingComparison}
                  onChange={checked => setIsChecked(checked)}
                  label={t("Exclude Non-Working Days")}
                />
              </Flex>
            </Flex>

            <div style={{ minHeight: "400px" }}>
              <LineChart
                data={timeComparisonData}
                xField="date"
                yField="number"
                seriesField="name"
                colorField="name"
                xAxis={{
                  label: {
                    formatter: v => dateTimeFunction({ date: v, format: DATE_MONTH_FORMAT }),
                  },
                }}
                yAxis={{
                  label: { formatter: v => numberConvertor(v, "intl", 2) },
                }}
                tooltip={{
                  customItems: item => renderChartTooltip(item),
                }}
                disabled={fetchingComparison}
                legend={{ position: "bottom" }}
                loading={fetchingComparison}
                animation={false}
                lineStyle={({ name }) => {
                  if (name === "Top Performer") {
                    return {
                      lineDash: [4, 4],
                      opacity: 0.7,
                    }
                  }
                }}
              />
            </div>
          </>
        )}
      </Modal>
    </span>
  )
}
export default ComparityModal
